import { memo } from 'react';

import { Link as ReactRouterLink, useParams } from 'react-router-dom';

import SettingsIcon from '@mui/icons-material/Settings';
import { Link, Stack, Typography } from '@mui/material';

import InkLinkButton from '@/components/shared/InkLinkButton/InkLinkButton';
import usePrimaryNavStore from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import { useInAppNotificationFeed } from '@/providers/InAppNotificationFeedProvider';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';

const NotificationFeedHeader = () => {
  const { clientId = '' } = useParams();
  const snackbar = useSnackbar();
  const { isDrawerLocked, setIsDrawerOpen } = usePrimaryNavStore();
  const { feedClient, hasNotificationItems, unreadCount, setFeedAnchorEl } =
    useInAppNotificationFeed();

  const handleLinkClick = () => {
    setFeedAnchorEl(null);

    if (!isDrawerLocked) {
      setIsDrawerOpen(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      p={2}
      sx={{
        position: 'sticky',
        bgcolor: 'neutral00',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="subtitle1" fontWeight="600">
        Notifications
      </Typography>

      <Stack direction="row" alignItems="center" gap={1.5}>
        <InkLinkButton
          onClick={() => {
            feedClient.markAllAsRead().catch(() => {
              snackbar({
                message:
                  'There was an error marking all notifications as read.',
                severity: 'error',
              });
            });
          }}
          sx={(theme) => ({
            ...theme.typography.body2,
            color: theme.palette.primary.dark,
            letterSpacing: 'normal',
            '&.markAllAsRead': {
              cursor: hasNotificationItems ? 'pointer' : 'not-allowed',
              pointerEvents: 'auto',
            },
            // TODO: remove this once bulk action is decided
            display: 'none',
          })}
          buttonProps={{
            className: 'markAllAsRead',
            disabled: !hasNotificationItems || unreadCount === 0,
            'data-cy': 'notification-mark-all-as-read',
          }}
        >
          Mark All As Read
        </InkLinkButton>

        <Link
          component={ReactRouterLink}
          to={`/${clientId}/notifications`}
          onClick={() => handleLinkClick()}
          data-cy="notification-settings-link"
        >
          <SettingsIcon
            color="action"
            fontSize="small"
            sx={{ display: 'flex', alignSelf: 'center' }}
          />
        </Link>
      </Stack>
    </Stack>
  );
};

export default memo(NotificationFeedHeader);
